import React from 'react';
import { Box, keyframes } from '@mui/material';

// Keyframe animations
const rotate = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
`;

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const walk = keyframes`
  0%, 100% {
    transform: translateY(0) translateX(0);
  }
  25% {
    transform: translateY(-8px) translateX(2px);
  }
  50% {
    transform: translateY(-4px) translateX(4px);
  }
  75% {
    transform: translateY(-8px) translateX(6px);
  }
`;

const walkLegs = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  25% {
    transform: translateY(-2px);
  }
  50% {
    transform: translateY(0);
  }
  75% {
    transform: translateY(-2px);
  }
`;

const bearEyes = keyframes`
  0%, 90%, 100% {
    transform: scaleY(1);
  }
  95% {
    transform: scaleY(0.1);
  }
`;

const bearTail = keyframes`
  0%, 100% {
    transform: rotate(0deg);
  }
  50% {
    transform: rotate(15deg);
  }
`;

const bearArms = keyframes`
  0%, 100% {
    transform: rotate(0deg);
  }
  50% {
    transform: rotate(5deg);
  }
`;

interface LoaderProps {
  size?: number;
  variant?: 'rotate' | 'pulse' | 'bounce' | 'shimmer' | 'walk';
  color?: string;
  text?: string;
}

const Loader: React.FC<LoaderProps> = ({ 
  size = 80, 
  variant = 'walk',
  color = '#30c',
  text = 'Loading...'
}) => {
  const getAnimation = () => {
    switch (variant) {
      case 'pulse':
        return `${pulse} 2s ease-in-out infinite`;
      case 'bounce':
        return `${bounce} 1.5s ease-in-out infinite`;
      case 'shimmer':
        return `${shimmer} 2s linear infinite`;
      case 'walk':
        return `${walk} 1.2s ease-in-out infinite`;
      default:
        return `${rotate} 2s linear infinite`;
    }
  };

  const getShimmerStyles = () => {
    if (variant === 'shimmer') {
      return {
        background: `linear-gradient(90deg, ${color} 0px, #fff 40px, ${color} 80px)`,
        backgroundSize: '200px 100%',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
      };
    }
    return {};
  };

  const getBearStyles = () => {
    if (variant === 'walk') {
      return {
        '& .bear-legs': {
          animation: `${walkLegs} 1.2s ease-in-out infinite`,
        },
        '& .bear-eyes': {
          animation: `${bearEyes} 3s ease-in-out infinite`,
        },
        '& .bear-tail': {
          animation: `${bearTail} 1.2s ease-in-out infinite`,
        },
        '& .bear-arms': {
          animation: `${bearArms} 1.2s ease-in-out infinite`,
        },
      };
    }
    return {};
  };

  const renderSebearBear = () => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 200 200"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="2" dy="4" stdDeviation="3" floodColor="rgba(0,0,0,0.2)"/>
        </filter>
      </defs>
      
      {/* Blue background inspired by Sebear */}
      <rect width="200" height="200" fill={color} rx="20" />
      
      {/* Bear Body - inspired by Sebear style but unique */}
      <ellipse 
        cx="100" cy="120" rx="45" ry="35" 
        fill="#fff" 
        filter="url(#shadow)"
        className="bear-body"
      />
      
      {/* Bear Head */}
      <circle 
        cx="100" cy="80" r="30" 
        fill="#fff" 
        filter="url(#shadow)"
        className="bear-head"
      />
      
      {/* Bear Ears - inspired by Sebear but more rounded */}
      <circle cx="85" cy="65" r="12" fill="#fff" className="bear-ears" />
      <circle cx="115" cy="65" r="12" fill="#fff" className="bear-ears" />
      <circle cx="85" cy="65" r="6" fill={color} opacity="0.3" />
      <circle cx="115" cy="65" r="6" fill={color} opacity="0.3" />
      
      {/* Bear Eyes */}
      <ellipse cx="92" cy="75" rx="4" ry="6" fill={color} className="bear-eyes" />
      <ellipse cx="108" cy="75" rx="4" ry="6" fill={color} className="bear-eyes" />
      <circle cx="92" cy="73" r="1.5" fill="#fff" />
      <circle cx="108" cy="73" r="1.5" fill="#fff" />
      
      {/* Bear Nose */}
      <ellipse cx="100" cy="85" rx="3" ry="2" fill={color} />
      
      {/* Bear Mouth */}
      <path d="M 95 90 Q 100 95 105 90" stroke={color} strokeWidth="1.5" fill="none" />
      
      {/* Bear Arms */}
      <ellipse cx="70" cy="110" rx="8" ry="15" fill="#fff" className="bear-arms" />
      <ellipse cx="130" cy="110" rx="8" ry="15" fill="#fff" className="bear-arms" />
      
      {/* Bear Legs */}
      <ellipse cx="85" cy="150" rx="10" ry="15" fill="#fff" className="bear-legs" />
      <ellipse cx="115" cy="150" rx="10" ry="15" fill="#fff" className="bear-legs" />
      
      {/* Bear Feet */}
      <ellipse cx="85" cy="160" rx="8" ry="5" fill="#fff" />
      <ellipse cx="115" cy="160" rx="8" ry="5" fill="#fff" />
      
      {/* Bear Tail */}
      <circle cx="140" cy="130" r="12" fill="#fff" className="bear-tail" />
      <circle cx="140" cy="130" r="6" fill={color} opacity="0.3" />
      
      {/* Bear Belly */}
      <ellipse cx="100" cy="125" rx="20" ry="15" fill={color} opacity="0.1" />
    </svg>
  );

  const renderShimmerBear = () => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 200 200"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="shimmerGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" style={{ stopColor: '#fff', stopOpacity: 0.3 }} />
          <stop offset="50%" style={{ stopColor: '#fff', stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: '#fff', stopOpacity: 0.3 }} />
        </linearGradient>
      </defs>
      
      {/* Blue background */}
      <rect width="200" height="200" fill={color} rx="20" />
      
      {/* Shimmer bear elements */}
      <ellipse cx="100" cy="120" rx="45" ry="35" fill="url(#shimmerGradient)" />
      <circle cx="100" cy="80" r="30" fill="url(#shimmerGradient)" />
      <circle cx="85" cy="65" r="12" fill="url(#shimmerGradient)" />
      <circle cx="115" cy="65" r="12" fill="url(#shimmerGradient)" />
      <ellipse cx="70" cy="110" rx="8" ry="15" fill="url(#shimmerGradient)" />
      <ellipse cx="130" cy="110" rx="8" ry="15" fill="url(#shimmerGradient)" />
      <ellipse cx="85" cy="150" rx="10" ry="15" fill="url(#shimmerGradient)" />
      <ellipse cx="115" cy="150" rx="10" ry="15" fill="url(#shimmerGradient)" />
      <circle cx="140" cy="130" r="12" fill="url(#shimmerGradient)" />
    </svg>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        minHeight: '200px',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: size,
          height: size,
          animation: getAnimation(),
          ...getShimmerStyles(),
          ...getBearStyles(),
        }}
      >
        {variant === 'shimmer' ? renderShimmerBear() : renderSebearBear()}
      </Box>
      
      {text && (
        <Box
          sx={{
            color: 'text.secondary',
            fontSize: '1rem',
            fontWeight: 500,
            textAlign: 'center',
            animation: variant === 'shimmer' ? `${shimmer} 2s linear infinite` : 'none',
            ...(variant === 'shimmer' && {
              background: `linear-gradient(90deg, #666 0px, #fff 40px, #666 80px)`,
              backgroundSize: '200px 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }),
          }}
        >
          {text}
        </Box>
      )}
    </Box>
  );
};

export default Loader;